'use client'
import { Alert, AlertTitle, Box } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import ParentCard from 'src/components/era-package/shared/ParentCard'
import pathMap from 'src/paths/pathMap'
import AbiTradePartnerApiUrls from '../../../../../apiUrls/AbiTradePartnerApiUrls'
import SnackBar from '../../../../../components/era-package/era/SnackBar'
import AbiUserSettingsApiUrls from '../../../../../apiUrls/AbiUserSettingsApiUrls'
import {
  header,
  optionsMap,
  importerInformation
} from 'src/views/abi/fields/create-trade-partner'
import RenderFormWithRHF from 'src/components/era-package/forms/RenderFormWithRHF'

const Wrapper = ({ readOnly, children }) => {
  if (readOnly) return <>{children}</>
  return (
    <ParentCard title='Trade Partner Details'>
      <Alert severity='info' sx={{ mb: 2 }}>
        <AlertTitle>Note</AlertTitle>
        This form captures all required data elements. Fields marked with{' '}
        <Box component='span' color='error.main'>
          *
        </Box>{' '}
        are mandatory.
      </Alert>
      {children}
    </ParentCard>
  )
}

const EditTradePartner = ({ readOnly, id }) => {
  const history = useHistory()
  const [isFetching, setFetching] = useState(false)
  const [fetchedData, setFetchedData] = useState()
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false)
  const [showSubmitErrorSnackbar, setShowSubmitErrorSnackbar] = useState(false)
  const [userSetting, setUserSetting] = useState(null)

  const fetchUserSetting = async () => {
    try {
      const response = await fetch(`${AbiUserSettingsApiUrls.GET}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        }
      })

      if (!response.ok) {
        console.log('Error fetching user setting')
        return { contactName: '', contactPhone: '' }
      }

      const json = await response.json()
      return json.data
    } catch (error) {
      console.log('Error fetching user setting')
      return { contactName: '', contactPhone: '' }
    }
  }

  // TODO set defaultValues combined, async
  useEffect(() => {
    let isAlive = true

    fetch(AbiTradePartnerApiUrls.GET + `?id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      }
    })
      .then((res) => res.json())
      .then((json) => {
        if (isAlive && !!json.data) {
          let parse = json.data
          console.log(parse)
          fetchUserSetting().then((r) => {
            if (parse.bondExpirationDate) {
              parse.bondExpirationDate = new Date(
                parse.bondExpirationDate
              )
            }
            setFetchedData(parse)
            setFetching(true)
          })
        }
      })
      .catch((err) => {
        console.error(err)
        setFetching(true)
      })

    return () => {
      isAlive = false
      setFetching(true)
    }
  }, [])

  // Set up form sections including table sections
  const formSections = useMemo(
    () => [
      {
        fields: header,
        headless: true,
        disableGutters: true
      },
      {
        title: 'Importer Information',
        fields: importerInformation,
        disableGutters: true,
        condition: (formData) => formData.partyCode === 'IM'
      }
    ],
    []
  )

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        console.log(userSetting?.filerCode)
        section.fields.map(
          (f) =>
            (f.defaultValue =
              f.name === 'filerCode' ? userSetting?.filerCode : '')
        )
        return section.fields
      }),
    [formSections, userSetting] // Make sure to update the dependency array
  )

  const handleSubmit = async (formData) => {
    try {
      const response = await fetch(AbiTradePartnerApiUrls.PUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify(formData) // Convert JS object to JSON string
      })
      if (!response.ok) {
        console.error('Error fetching user data')
        setShowSubmitErrorSnackbar(true)
        return false
      }
      const result = await response.json()
      setShowSuccessSnackbar(formData.id)
      console.log('submitted:', result)
      return true
    } catch (error) {
      console.error('Error fetching user data:', error)
    }
  }

  // Handle snackbar close
  const handleSuccessSnackbarClose = (e, reason) => {
    if (reason === 'timeout') {
      history.push(
        pathMap.ABI_TRADE_PARTNER_VIEW.replace(
          ':id',
          showSuccessSnackbar
        )
      )
      setShowSuccessSnackbar(false)
    }
  }

  // Handle snackbar close
  const handleSubmitErrorSnackbarClose = useCallback(() => {
    setShowSubmitErrorSnackbar(false)
  }, [])

  if (!isFetching) return <p>Loading user data...</p>

  return (
    <Wrapper readOnly={readOnly}>
      <RenderFormWithRHF
        title='Basic Information'
        formSections={formSections}
        allFields={allFields}
        optionsMap={optionsMap}
        submitLabel='Update'
        onSubmit={handleSubmit}
        sx={{ border: 0 }}
        formConfig={{}}
        initialData={fetchedData}
        readOnly={readOnly}
        hideSave={true}
      />
      {/* Error Snackbar - Only show when showErrorSnackbar is true */}
      <SnackBar
        title='Form Submittion Error'
        type='error'
        open={showSubmitErrorSnackbar}
        onClose={handleSubmitErrorSnackbarClose}
        duration={10000}
      >
        Internal error occured, please try again later.
      </SnackBar>

      {/* Success Snackbar - Only show when showSuccessSnackbar is true */}
      <SnackBar
        title='Form submitted successfully'
        type='success'
        open={!!showSuccessSnackbar}
        onClose={handleSuccessSnackbarClose}
        duration={3000}
      >
        Redirecting to Trade Partner Summary...
      </SnackBar>
    </Wrapper>
  )
}

export default EditTradePartner
